import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { getAccessToken } from 'utils/accessToken';
import { getActiveScopes, validateScopes } from 'utils/scopes';
import { getOpParams } from '../getOpParams';

/**
 * Custom hook to validate if the user has the required scopes to access a specific feature or page.
 * It fetches the organization and access token details, determines the active scopes for the user,
 * and then validates these scopes against the allowed scopes for the current context.
 *
 * @param allowedScopes - An array of scopes required to access the feature or page.
 * @returns An object containing:
 *   - `isValid`: A boolean indicating whether the user has the required scopes.
 *   - `isLoading`: A boolean indicating whether the data required for validation is still loading.
 */
export const useValidateScopes = (allowedScopes: string[]) => {
  const { orgId } = getOpParams();
  const accessToken = getAccessToken();

  // Fetch the current org information
  const { data: orgData, isPending: isOrgIdPending } = useOpQuery({
    apiEndpointName: 'describeOrg',
    parameters: [orgId],
    select: (data) => data.json.data,
  });

  const { data: tokenData, isPending: isTokenPending } = useOpQuery({
    apiEndpointName: 'describeAccessToken',
    parameters: [accessToken],
    select: (data) => data.json.data,
  });

  // Get org & identity scopes
  const orgScopes = getActiveScopes({
    tokenScopeList: tokenData?.tokenScopeList ?? [],
    orgId: Number(orgId),
    parentOrgId: orgData?.parentOrg?.id,
  });

  // Get partner-level scopes
  const partnerScopes = getActiveScopes({
    tokenScopeList: tokenData?.tokenScopeList ?? [],
    parentOrgId: orgData?.parentOrg?.id,
  });

  // Get all the applicable scopes for the user and current org
  const activeScopes = [...orgScopes, ...partnerScopes];

  // Validate the user's applicable scopes against the current page's scopes
  const userHasAllowedScopes = validateScopes({
    activeScopes,
    orgId,
    rawAllowedScopes: allowedScopes,
  });

  return {
    // If allowedScopes is empty, we need to assume the user has access. Regression potentially exposed in https://github.com/msi-alta/platinum/commit/0b92d3d5cc6b9b25e98de73f36964d9f402fb8f6
    isValid: !allowedScopes.length || userHasAllowedScopes,
    isLoading: Boolean(accessToken) && (isOrgIdPending || isTokenPending),
  };
};
